// Pure mapping from the assistant's neutral apply payload to the landing dashboard's
// `dashboardFilterParams`. Extracted from dashboard/index.jsx so the trickiest, highest-risk logic
// (dimension->paramKey, action set/add/remove, wildcard operator + cost-field aliasing, year,
// clear_all) is unit-testable without rendering the dashboard. The dashboard method is now a thin
// wrapper that calls this, then does I/O (sessionStorage + setState + refresh).
//
// Does NOT mutate `current` — it returns a fresh params object.

// Operator translation is NOT dashboard-specific — the asset filter window stores the same
// `{ key, filters: [operator] }` entry out of the same vocabulary — so it lives in core and is shared with
// assets/ottoAssetFilters.js. It used to live here, which is precisely why the asset adapter grew its own
// (raw, untranslated) version. See core/wildcardOperators.js.
import { toPanelWildcardEntry } from "../../core/wildcardOperators";

// Backend wildcard field id -> the landing dashboard's catalog `key` (naming differs in places).
export const WILDCARD_ALIAS = { opinions_cost: "options_cost", project_costs: "project_total" };

// The dashboard's VIEW-MODE toggles (the filter window's COST / STATIC FCI / Dashboard Source pills),
// surfaced to Otto as scalar toggles. Unlike record-level filters these set dashboard-level params, so the
// apply layer translates them explicitly. `display` powers Otto's chip; `apply` mutates the params object
// (an absent value means "default", so we delete the param rather than send a default).
export const VIEW_TOGGLES = {
    project_cost: {
        label: "Cost basis",
        display: { construction: "Construction", project: "Project" },
        apply: (base, value) => {
            if (value === "project") base.project_cost = true;
            else delete base.project_cost; // construction / cleared = dashboard default
        }
    },
    update_fci: {
        label: "Static FCI",
        display: { on: "On", off: "Off" },
        apply: (base, value) => {
            if (value === "off") base.update_fci = false;
            else delete base.update_fci; // on / cleared = dashboard default
        }
    },
    csp_status: {
        label: "Dashboard source",
        display: { recommendation: "Recommendation", capital_request: "Capital Request", both: "Both" },
        apply: (base, value) => {
            const map = { recommendation: ["recommendation"], capital_request: ["capital_request"], both: ["recommendation", "capital_request"] };
            if (map[value]) base.csp_status = map[value];
            else delete base.csp_status; // cleared = dashboard default (recommendation)
        }
    }
};

// Human-readable chip text for a scalar toggle Otto resolved (used by the presenter).
export const describeViewToggle = (key, value) => {
    const t = VIEW_TOGGLES[key];
    if (!t) return `${key}: ${value}`;
    return `${t.label}: ${(t.display && t.display[value]) || value}`;
};

// ---------------------------------------------------------------------------------------------------
// QA-band scalar filters — a RECORD-level filter the API takes as a TOP-LEVEL param.
//
// ⚠️ CURRENTLY UNREACHABLE (FCA-7464, merged 2026-09-15). The dashboard's QA/QC band was rebuilt: the two
// fields below were DELETED from `initialMaterFilters` and replaced by ordinary list/wildcard filters
// (QA/QC Issue, Reviewer, Issue Status, QA Status, Resolved/Added Date, Comments), none of which uses this
// shape. Grounding requires the field to exist in the live `filterFields`, so nothing reaches this path
// today. It is kept rather than deleted because the MECHANISM is still correct and the API still honors it
// — `by_review_status(params[:review_status])` / `by_qa_passed(params[:qa_passed])` are both still in
// recommendations_controller#get_list, and the JSONB/boolean columns still exist. If either field returns
// to the filter window, this works as written; if a new QA field ever needs a top-level param, add its key
// here rather than inventing a second scalar path. Everything below describes that original pair.
//
// "My QA" (review_status) and "QA Passed" (qa_passed) are ordinary record filters that the dashboard's
// filter window renders as a single-choice dropdown. They used to reach Otto's card as an inert "not applied
// yet (coming next)" chip, so "show me my pending QA issues" produced a card that could do nothing.
//
// Their wire shape is NOT the wildcard shape the rest of that window uses. Verified against the Rails
// dashboard chain (projects_controller#dashboard) and the option endpoint Otto counts with
// (recommendations_controller#get_list) — BOTH read them top-level:
//     .by_qa_passed(params[:qa_passed])          -> recommendations.qa_passed IS true/false
//     .by_review_status(params[:review_status])  -> (review_status -> 'review_status' ->> '<value>')::bool
// `review_status` is a JSONB column holding {"review_status":{"pending":true,…}}, so the generic
// `filters[review_status] = { key: "pending", filters: ["equals"] }` wildcard compare the filter window
// stores can never match a row — an "applied" QA filter came back empty. Otto therefore sends what the API
// READS, and both the apply and the record-count probe agree because both endpoints honor the same param.
// The allowed values are exactly the dropdown's option values, which is also what Otto grounds against.
//
// Grounding is unchanged from every other pick: the value must be one of the options the FIELD ITSELF
// offers this user and project (the dashboard's live `options`) — never a hard-coded enum — and a field the
// user's role can't see (`hide`) stays unapplyable.
// ---------------------------------------------------------------------------------------------------
export const SCALAR_PARAM_FILTERS = { review_status: true, qa_passed: true };

// Is this field one of them? (Either naming: Otto's field key or the dashboard's param key — they match for
// both fields today, and checking both keeps a rename from silently falling back to the wildcard shape.)
export const isScalarParamFilter = field =>
    !!field && (!!SCALAR_PARAM_FILTERS[field.key || field.id] || !!SCALAR_PARAM_FILTERS[field.paramKey]);

// A field-like is a select field whether it comes from the dashboard's raw config (`fieldType`) or Otto's
// augmented wildcard map (`value_type`).
const isSelectField = field => !!field && (field.fieldType === "select" || field.value_type === "select");

// Compare enum spellings loosely: "all_pending" == "All Pending". The interpreter emits the backend's
// snake_case value, but a user-echoed label is just as plausible and shouldn't be silently dropped.
const looseValue = v =>
    String(v == null ? "" : v)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

// Every option the field really offers (the placeholder — empty value, the dropdown's "Select" — is not an
// option: it means "no filter"). Used for grounding AND for the pill's own editor.
export const selectOptionsOf = field =>
    (((field && (field.options || field.inlineOptions)) || []).filter(o => o && o.value !== "" && o.value != null));

// The option a scalar value names on a select field, or null. Value first, then the visible label.
export const selectScalarOption = (field, value) => {
    if (!isSelectField(field) || !isScalarParamFilter(field)) return null;
    // `hide` is the filter window's own per-role gate (My QA / QA Passed are hidden from a client_user).
    // DEFENCE IN DEPTH: buildAvailableFields now drops hidden fields from the offer surface entirely, so the
    // model is never told this field exists — but the maps deliberately keep it (so an already-applied filter
    // can still be described), and this is the boundary where "describable" must not become "applyable".
    if (field.hide) return null;
    const want = looseValue(value);
    if (!want) return null;
    const opts = selectOptionsOf(field);
    return opts.find(o => looseValue(o.value) === want) || opts.find(o => looseValue(o.label) === want) || null;
};

// Resolve against the dashboard's LIVE field configs (the apply side, which needs the param key).
export const scalarSelectFromFields = (key, value, filterFields = []) => {
    const cfg = (filterFields || []).find(f => f && (f.key === key || f.paramKey === key));
    const option = selectScalarOption(cfg, value);
    if (!option) return null;
    // The API param, not the wildcard storage key: these two filters are read top-level (see above).
    const paramKey = cfg.paramKey || cfg.key;
    return paramKey ? { field: cfg, option, paramKey } : null;
};

// Same resolution from Otto's augmented maps (the render side — a card has `maps`, not `filterFields`).
export const scalarSelectFromMaps = (key, value, maps) => {
    const meta = maps && maps.wildcardMap ? maps.wildcardMap[key] : null;
    const option = selectScalarOption(meta, value);
    if (!option) return null;
    return { field: meta, option, paramKey: meta.paramKey || key };
};

// Write one of these filters into the dashboard's params (or clear it). One function so the three callers —
// a scalar in the payload, the same filter arriving as a wildcard pill, and a "remove the QA filter" clear —
// can never disagree about the wire shape.
export const applyScalarParamFilter = (base, paramKey, value) => {
    if (value == null || value === "") delete base[paramKey];
    else base[paramKey] = String(value);
};

// Seed the applied-filter TAG for one of these filters. The tag area resolves a param's value to a display
// name through `masterFilterList[field.key]` — a list loaded only when the user opens that field's dropdown,
// which for an Otto apply never happens, so the tag would render with no value and be dropped. We already
// know the option, so hand it back the same way the dimension path does (mirroring the state a manual pick
// leaves behind). `seeds` is the labelSeeds accumulator.
const seedScalarParamLabel = (seeds, field, option) => {
    if (!field || !field.key || !option) return;
    (seeds.options[field.key] = seeds.options[field.key] || []).push({
        id: option.value,
        name: option.label != null ? option.label : option.value
    });
    seeds.fields.push(field);
};

// Does this scalar name a filter Otto can actually put on the dashboard? Asked before anything is promised:
// a yes becomes a real pill/chip and can enable Apply, a no keeps the honest "left it out" note.
export const isApplyableScalar = (scalar, maps) =>
    !!scalar && !!scalar.key && (!!VIEW_TOGGLES[scalar.key] || !!scalarSelectFromMaps(scalar.key, scalar.value, maps));

// Is this scalar a RECORD-level filter (rides an opt-in pill, counted like any filter) rather than a
// dashboard VIEW toggle (a chip that follows the request)? The card splits on this.
export const isRecordScalarFilter = (scalar, maps) =>
    !!scalar && !VIEW_TOGGLES[scalar.key] && !!scalarSelectFromMaps(scalar.key, scalar.value, maps);

// Chip/pill text for any scalar Otto extracted, in the dashboard's OWN words: the view-toggle wording
// ("Cost basis: Project"), else "<field label>: <option label>" ("My QA: Pending"), else the raw
// key: value pair as a last resort (an unmapped scalar, which the card also labels as not applied).
export const describeScalar = (scalar, maps) => {
    const { key, value } = scalar || {};
    if (VIEW_TOGGLES[key]) return describeViewToggle(key, value);
    const hit = scalarSelectFromMaps(key, value, maps);
    if (hit) return `${(hit.field && hit.field.label) || key}: ${hit.option.label || hit.option.value}`;
    return `${key}: ${String(value)}`;
};

export const buildDashboardFilterParams = ({ payload, filterFields = [], current = {} }) => {
    const byGetListKey = {};
    filterFields.forEach(f => {
        if (f.getListKey) byGetListKey[JSON.stringify(f.getListKey)] = f;
    });

    // Diagnostics for end-to-end tracing: which dimensions/wildcards actually mapped to a dashboard
    // paramKey, and which were DROPPED (no field config, or a config without a paramKey). The silent-drop
    // is the worst apply bug — Otto says "Applied" while the dashboard never gets the param — so the apply
    // span marks any non-empty `dropped` as an error. Does not affect what gets applied.
    const diagnostics = { applied: [], dropped: [], wildcardsApplied: [], wildcardsDropped: [], clears: [], year: null, scalarsApplied: [], scalarsDropped: [] };

    // Label seeds: the dashboard renders applied-filter TAGS (+ the FilterModal "all filters" view) by
    // re-deriving each option's display name from `masterFilterList[field.key]` — a list loaded LAZILY,
    // only when the user opens that field's dropdown. Otto applies option ids without ever loading it, so
    // the id->name lookup returns nothing and the tag is dropped (only "Years", special-cased, always
    // shows). We already know the name (d.name, straight from resolution) and the full field config (cfg,
    // covering bands outside the base filterFields), so we hand both back for the caller to seed into
    // redux — mirroring the state a MANUAL apply leaves behind. `fields` fixes the priority/dynamic bands
    // (no config -> no tag at all); `options` fixes the common fields (config present -> empty value tag).
    const labelSeeds = { fields: [], options: {} };

    // clear_all: drop everything except the project/client scope (and reset queries via clearedAll).
    if (payload.clear_all) {
        const kept = { exclude_filters: {} };
        if (current.project_ids) kept.project_ids = current.project_ids;
        if (current.client_ids) kept.client_ids = current.client_ids;
        return { params: kept, clearedAll: true, diagnostics: { ...diagnostics, clearAll: true } };
    }

    const base = { ...current };
    base.exclude_filters = { ...(current.exclude_filters || {}) };

    // Honor the action: set replaces a dimension's values, add appends, remove subtracts.
    const setByKey = {};
    const addByKey = {};
    const removeByKey = {};
    (payload.dimensions || []).forEach(d => {
        const cfg = byGetListKey[JSON.stringify(d.get_list_field)];
        // Apply the option's id — exactly what the dashboard stores when a user clicks an option
        // (FilterItem -> handleChange(item.id)) — falling back to the name only when the option has no id
        // (true value-enum dimensions whose param is the value string). The old
        // `value_kind === "id" ? id : name` heuristic sent the NAME for EVERY single-column ("value")
        // dimension, which is wrong for priority-band elements and any field whose option id differs from
        // its label: "30 = Will close facility…" went into option_ids.2 as the value, so the dashboard
        // filtered on a value that isn't in the list and showed no data. Mirroring the dashboard fixes the class.
        const value = d.id !== null && d.id !== undefined ? d.id : d.name;
        if (!cfg || !cfg.paramKey) {
            diagnostics.dropped.push({
                get_list_field: d.get_list_field,
                value,
                reason: cfg ? "field config has no paramKey" : "no field config for get_list_field"
            });
            return;
        }
        if (value === null || value === undefined) {
            diagnostics.dropped.push({ get_list_field: d.get_list_field, paramKey: cfg.paramKey, reason: "null value" });
            return;
        }
        diagnostics.applied.push({ paramKey: cfg.paramKey, value, action: d.action || "set" });
        const bucket = d.action === "add" ? addByKey : d.action === "remove" ? removeByKey : setByKey;
        (bucket[cfg.paramKey] = bucket[cfg.paramKey] || []).push(value);
        // Seed the tag/label sources for everything we're actually adding to the dashboard (not removes).
        // Keyed by cfg.key — the same key `saveAppliedFilters`/`AppliedFilters` use for masterFilterList.
        if (cfg.key && d.action !== "remove") {
            (labelSeeds.options[cfg.key] = labelSeeds.options[cfg.key] || []).push({
                id: value,
                name: d.name != null ? d.name : value
            });
            labelSeeds.fields.push(cfg);
        }
    });
    Object.keys(setByKey).forEach(k => {
        base[k] = [...new Set(setByKey[k])];
    });
    Object.keys(addByKey).forEach(k => {
        const existing = Array.isArray(base[k]) ? base[k] : [];
        base[k] = [...new Set([...existing, ...addByKey[k]])];
    });
    Object.keys(removeByKey).forEach(k => {
        // Compare by String: the id to remove comes from the catalog (e.g. "7") while the applied param may
        // hold it as a number (7) — a strict `includes` would silently fail to subtract it.
        if (Array.isArray(base[k])) {
            const drop = new Set(removeByKey[k].map(String));
            base[k] = base[k].filter(v => !drop.has(String(v)));
        }
    });

    (payload.clears || []).forEach(c => {
        const cfg = byGetListKey[JSON.stringify(c.get_list_field)];
        if (cfg && cfg.paramKey) {
            delete base[cfg.paramKey];
            diagnostics.clears.push({ paramKey: cfg.paramKey });
        } else {
            diagnostics.dropped.push({ get_list_field: c.get_list_field, reason: "clear: no field config / paramKey" });
        }
    });

    if (payload.year) {
        if (payload.year.start_year != null) base.start_year = payload.year.start_year;
        if (payload.year.end_year != null) base.end_year = payload.year.end_year;
        diagnostics.year = { start_year: payload.year.start_year, end_year: payload.year.end_year };
    }

    // "remove the year filter": the Years filter lives in start_year/end_year (not a dimension/wildcard), so
    // clearing it means dropping those params back to the dashboard default (no year clamp).
    if (payload.clearYear) {
        delete base.start_year;
        delete base.end_year;
        diagnostics.clears.push({ paramKey: "start_year" }, { paramKey: "end_year" });
    }

    // Wildcard (operator) filters -> base.filters[paramKey] = { key, filters: [operator] }
    const landingWildcards = filterFields.filter(f => f.isWildCardFilter);
    (payload.wildcards || []).forEach(w => {
        const aliasKey = WILDCARD_ALIAS[w.field_id] || w.field_id;
        const cfg = landingWildcards.find(f => f.key === aliasKey || f.paramKey === w.field_id);
        if (!cfg || !cfg.paramKey) {
            diagnostics.wildcardsDropped.push({ field_id: w.field_id, reason: "no wildcard field config" });
            return;
        }
        // A QA-band filter reaches the card as an opt-in PILL (so it is counted and only applies when the
        // user selects it), but its wire shape is the top-level param the API reads — never a `filters[…]`
        // entry, which cannot match its JSONB/boolean column. Grounded against the field's live options, so
        // an edited pill can't send a value the dropdown doesn't offer.
        if (isScalarParamFilter(cfg)) {
            const option = selectScalarOption(cfg, w.value);
            if (!option) {
                diagnostics.wildcardsDropped.push({ field_id: w.field_id, value: w.value, reason: "value not offered by the field" });
                return;
            }
            applyScalarParamFilter(base, cfg.paramKey, option.value);
            seedScalarParamLabel(labelSeeds, cfg, option);
            diagnostics.wildcardsApplied.push({ paramKey: cfg.paramKey, operator: "equals", value: option.value });
            return;
        }
        const { entry, operator: op, dropped } = toPanelWildcardEntry({
            operator: w.operator,
            value: w.value,
            valueRange: w.value_range,
            fieldType: cfg.fieldType
        });
        if (dropped) {
            diagnostics.wildcardsDropped.push({ field_id: w.field_id, operator: w.operator, reason: dropped });
            return;
        }
        base.filters = { ...(base.filters || {}) };
        base.filters[cfg.paramKey] = entry;
        diagnostics.wildcardsApplied.push({ paramKey: cfg.paramKey, operator: op });
    });

    // Remove cleared wildcard filters (e.g. "remove the cost filter" -> clearWildcards: ["opinions_cost"]).
    (payload.clearWildcards || []).forEach(fieldId => {
        const aliasKey = WILDCARD_ALIAS[fieldId] || fieldId;
        const cfg = landingWildcards.find(f => f.key === aliasKey || f.paramKey === fieldId);
        if (!cfg || !cfg.paramKey) return;
        // A QA-band filter lives in a top-level param, so "remove the QA filter" has to clear it there —
        // deleting a `filters[…]` entry that was never written would leave it applied forever.
        if (isScalarParamFilter(cfg)) {
            applyScalarParamFilter(base, cfg.paramKey, "");
            diagnostics.clears.push({ paramKey: cfg.paramKey });
            return;
        }
        if (base.filters && base.filters[cfg.paramKey]) {
            base.filters = { ...base.filters };
            delete base.filters[cfg.paramKey];
        }
    });

    // Scalars, two kinds. A VIEW toggle (COST / STATIC FCI / Dashboard Source) sets a dashboard-level param.
    // A QA-band filter (My QA, QA Passed) sets its own top-level param. Only a scalar that is neither is
    // dropped, with a diagnostic (the apply span flags it).
    (payload.scalars || []).forEach(sc => {
        if (!sc || !sc.key) return;
        const toggle = VIEW_TOGGLES[sc.key];
        if (toggle) {
            toggle.apply(base, sc.value);
            diagnostics.scalarsApplied.push({ key: sc.key, value: sc.value });
            return;
        }
        // A QA-band filter that arrived as a SCALAR (a confirmed plan step's payload, or any caller that
        // isn't the card). Same top-level param the pill path writes.
        const select = scalarSelectFromFields(sc.key, sc.value, filterFields);
        if (select) {
            applyScalarParamFilter(base, select.paramKey, select.option.value);
            seedScalarParamLabel(labelSeeds, select.field, select.option);
            diagnostics.scalarsApplied.push({ key: sc.key, value: select.option.value, paramKey: select.paramKey });
            return;
        }
        diagnostics.scalarsDropped.push({ key: sc.key, value: sc.value, reason: "scalar toggle not supported for apply" });
    });

    return { params: base, clearedAll: false, diagnostics, labelSeeds };
};
